// Platform Admin — Aayush AI Engine (Gemini BYOK) Panel
// Lets the platform owner plug in their OWN Gemini API key so Aayush runs on it
// (zero Emergent credits). If empty, Aayush falls back to the Emergent key.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NOTE: we use axios (XHR-based) instead of fetch here. Emergent's telemetry
// wrapper around window.fetch pre-consumes error-response bodies, so fetch's
// r.json()/r.text() throw 'body stream already read' on non-2xx responses.
// axios bypasses that wrapper and exposes the parsed error body via err.response.data.

const T = {
  hi: {
    title: 'आयुष AI इंजन — अपनी Gemini Key',
    subtitle: 'अपनी खुद की Google Gemini API key लगाएँ। इससे आयुष आपकी ही key पर चलेगा और Emergent credits बिल्कुल खर्च नहीं होंगे।',
    statusOn: 'आयुष इस वक्त आपकी OWN Gemini Key पर चल रहा है ✅',
    statusOff: 'आयुष अभी Emergent Universal Key पर चल रहा है (आपकी key नहीं लगी है)',
    model: 'मॉडल',
    placeholder: 'AIzaSy… (Google AI Studio से मिली Gemini API key)',
    save: 'Validate करके Save करें',
    saving: 'जाँच हो रही है…',
    remove: 'Key हटाएँ (Emergent पर वापस)',
    getKey: 'Gemini API key यहाँ से लें →',
    note: 'Key DB में सुरक्षित रहती है और redeploy के बाद भी बनी रहती है। Schools अपने स्कूल AI के लिए अपनी अलग key देंगे।',
    updated: 'आख़िरी अपडेट',
    emptyErr: 'कृपया अपनी Gemini API key डालें',
  },
  en: {
    title: 'Aayush AI Engine — Your Gemini Key',
    subtitle: "Plug in your own Google Gemini API key. Aayush runs on YOUR key and consumes zero Emergent credits.",
    statusOn: 'Aayush is currently running on your OWN Gemini Key ✅',
    statusOff: 'Aayush is running on the Emergent Universal Key (your key is not set)',
    model: 'Model',
    placeholder: 'AIzaSy… (Gemini API key from Google AI Studio)',
    save: 'Validate & Save',
    saving: 'Validating…',
    remove: 'Remove key (revert to Emergent)',
    getKey: 'Get a Gemini API key →',
    note: 'The key is stored securely in the DB and survives redeploys. Schools provide their own separate key for their school AI.',
    updated: 'Last updated',
    emptyErr: 'Please enter your Gemini API key',
  },
};

export default function AayushAIKeyPanel({ token, language = 'hi' }) {
  const t = T[language] || T.hi;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ configured: false, masked_key: '', model: '', updated_at: null });
  const [keyInput, setKeyInput] = useState('');
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/platform-admin/aayush-ai-key`, { headers: { Authorization: `Bearer ${token}` } });
      if (data.success) setStatus(data);
    } catch (e) { setMsg({ type: 'error', text: 'Load failed: ' + (e?.response?.data?.detail || e.message) }); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!keyInput.trim()) { setMsg({ type: 'error', text: t.emptyErr }); return; }
    setSaving(true); setMsg(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/platform-admin/aayush-ai-key`,
        { api_key: keyInput.trim() },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setMsg({ type: 'success', text: data.message });
        setKeyInput('');
        await load();
      } else {
        setMsg({ type: 'error', text: data.detail || data.message || 'Save failed' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || e.message || 'Save failed' });
    }
    setSaving(false);
  };

  const remove = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await axios.delete(`${API_URL}/api/platform-admin/aayush-ai-key`, { headers: { Authorization: `Bearer ${token}` } });
      if (data.success) { setMsg({ type: 'success', text: data.message }); await load(); }
    } catch (e) { setMsg({ type: 'error', text: e?.response?.data?.detail || e.message }); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-8" data-testid="aayush-key-loading">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="aayush-ai-key-panel">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">{t.title}</h2>
        <p className="text-sm text-slate-500 mt-1">{t.subtitle}</p>
      </div>

      {/* Status banner */}
      <div
        data-testid="aayush-key-status"
        className={`rounded-xl border p-4 flex items-start gap-3 ${
          status.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
        }`}
      >
        <span className="text-2xl leading-none">{status.configured ? '🔐' : '⚡'}</span>
        <div className="flex-1">
          <p className={`font-semibold ${status.configured ? 'text-emerald-700' : 'text-amber-700'}`}>
            {status.configured ? t.statusOn : t.statusOff}
          </p>
          {status.configured && (
            <p className="text-sm text-slate-600 mt-1">
              <span className="font-mono bg-white px-2 py-0.5 rounded border" data-testid="aayush-key-masked">{status.masked_key}</span>
              <span className="ml-3">{t.model}: <b>{status.model}</b></span>
            </p>
          )}
          {status.configured && status.updated_at && (
            <p className="text-xs text-slate-400 mt-1">{t.updated}: {new Date(status.updated_at).toLocaleString()}</p>
          )}
        </div>
      </div>

      {/* Key input */}
      <div className="space-y-3">
        <input
          type="password"
          data-testid="aayush-key-input"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder={t.placeholder}
          className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 font-mono"
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="aayush-key-save-btn"
            onClick={save}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {saving ? t.saving : t.save}
          </button>
          {status.configured && (
            <button
              data-testid="aayush-key-remove-btn"
              onClick={remove}
              disabled={saving}
              className="px-5 py-2.5 rounded-lg border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-60 transition-colors"
            >
              {t.remove}
            </button>
          )}
          <a
            href="https://aistudio.google.com/app/apikey"
            target="_blank"
            rel="noreferrer"
            data-testid="aayush-key-get-link"
            className="text-sm text-indigo-600 hover:underline ml-auto"
          >
            {t.getKey}
          </a>
        </div>
      </div>

      {msg && (
        <div
          data-testid="aayush-key-msg"
          className={`text-sm rounded-lg px-4 py-3 ${
            msg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <p className="text-xs text-slate-400 border-t pt-4">{t.note}</p>
    </div>
  );
}
